import React, { useRef, useState } from 'react';
import { Upload, X, Play, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';

interface HeroMediaUploadProps {
  value: {
    url: string;
    type: 'image' | 'video';
    thumbnailUrl?: string;
  } | null;
  onChange: (value: { url: string; type: 'image' | 'video'; thumbnailUrl?: string; } | null) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
}

export function HeroMediaUpload({
  value,
  onChange,
  onError,
  disabled = false
}: HeroMediaUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { user, profile } = useUnifiedAuth();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Validate file size (200MB limit)
      if (file.size > 200 * 1024 * 1024) {
        throw new Error('File size exceeds 200MB limit');
      }

      // Create file path
      const filePath = `${user.id}/${crypto.randomUUID()}-${file.name}`;
      
      // Create upload options
      const options = {
        upsert: true,
        contentType: file.type,
        cacheControl: '3600'
      };

      // Upload file with progress tracking
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
        }
      });

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('experience-media')
        .upload(filePath, file, options);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('experience-media')
        .getPublicUrl(uploadData.path);

      // Update component state
      onChange({
        url: publicUrl,
        type: file.type.startsWith('video/') ? 'video' : 'image'
      });

      // Create local preview
      setPreviewUrl(URL.createObjectURL(file));

    } catch (error) {
      console.error('Upload failed:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
    setPreviewUrl(null);
  };

  return (
    <div className="space-y-4">
      <div
        onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg transition-colors
          ${value ? 'aspect-video' : 'aspect-[21/9]'}
          ${disabled ? 'opacity-50 cursor-not-allowed' :
            isUploading ? 'opacity-50 cursor-wait' :
            'hover:border-sand-400 cursor-pointer'}
          border-earth-700 bg-earth-800/50
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,video/*"
          onChange={handleFileChange}
          disabled={disabled || isUploading}
        />

        {value ? (
          <div className="relative w-full h-full">
            {value.type === 'video' ? (
              <>
                <video
                  src={previewUrl || value.url}
                  poster={value.thumbnailUrl}
                  className="w-full h-full object-cover"
                  controls
                  muted
                  playsInline
                />
                <div className="absolute inset-0 flex items-center justify-center bg-earth-900/50 opacity-0 hover:opacity-100 transition-opacity">
                  <Play className="h-12 w-12 text-sand-100" />
                </div>
              </>
            ) : (
              <img
                src={previewUrl || value.url}
                alt="Hero media"
                className="w-full h-full object-cover"
              />
            )}

            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              disabled={isUploading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {isUploading ? (
              <>
                <Loader2 className="h-12 w-12 text-sand-400 animate-spin mb-4" />
                <p className="text-sm text-sand-300">Uploading... {uploadProgress}%</p>
              </>
            ) : (
              <>
                <Upload className="h-12 w-12 text-sand-400 mb-4" />
                <p className="text-sm text-sand-300">
                  Drop hero image/video here, or click to select
                </p>
                <p className="text-xs text-sand-400 mt-2">
                  Supported formats: JPG, PNG, WebP, MP4 (max 200MB)
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}